/**
 * Skip Link Component
 * 
 * Provides keyboard navigation shortcut to skip to main content
 * WCAG 2.1 AA compliant - allows users to bypass repetitive navigation
 */

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface SkipLinkProps {
  href?: string;
  targetId?: string;
  className?: string;
  children?: React.ReactNode;
}

export function SkipLink({ 
  href = "#main-content", 
  targetId = "main-content",
  className,
  children = "Skip to main content"
}: SkipLinkProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Show skip link when Tab is pressed (first keyboard interaction)
      if (e.key === "Tab" && !isVisible) {
        setIsVisible(true);
      }
    };

    const handleMouseDown = () => {
      // Hide skip link on mouse interaction
      setIsVisible(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isVisible]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      // Ensure focus is visible
      target.setAttribute("tabIndex", "-1");
      target.focus();
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className={cn(
        "skip-link",
        "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999]",
        "focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground",
        "focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        "focus:font-semibold focus:no-underline",
        isVisible && "not-sr-only absolute top-4 left-4 z-[9999]",
        className
      )}
      aria-label="Skip to main content"
    >
      {children}
    </a>
  );
}

